import React, { useState, useEffect } from 'react';

/**
 * PWA Install Banner Component
 * Shows a dismissible banner prompting users to install the app
 * Only shows when the beforeinstallprompt event fires (browser supports installation)
 */
export default function PWAInstallBanner() {
  // Admin-only feature: PWA install banner hidden from regular users
  return null;

  // eslint-disable-next-line no-unreachable
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Check if already dismissed
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      // Show again after 7 days
      if (now - dismissedDate < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // Check if already installed
    if (window.isPWA && window.isPWA()) {
      return;
    }

    // Listen for install prompt
    const handleInstallPrompt = (e) => {
      setDeferredPrompt(e.detail?.prompt || window.deferredPrompt);
      setShowBanner(true);
    };

    window.addEventListener('pwa-install-available', handleInstallPrompt);

    // Check if prompt is already available
    if (window.deferredPrompt) {
      setDeferredPrompt(window.deferredPrompt);
      setShowBanner(true);
    }

    // Listen for successful installation
    const handleInstalled = () => {
      setShowBanner(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('pwa-installed', handleInstalled);

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallPrompt);
      window.removeEventListener('pwa-installed', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt && !window.triggerPWAInstall) return;

    setIsInstalling(true);

    try {
      let outcome;
      if (window.triggerPWAInstall) {
        outcome = await window.triggerPWAInstall();
      } else {
        deferredPrompt.prompt();
        const result = await deferredPrompt.userChoice;
        outcome = result.outcome;
      }

      if (outcome === 'accepted') {
        setShowBanner(false);
      }
    } catch (error) {
      console.error('PWA install failed:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', new Date().toISOString());
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-fadeIn">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start gap-3">
          {/* App icon */}
          <div className="shrink-0 w-12 h-12 bg-linear-to-br from-decisiv-blue-light to-decisiv-blue rounded-xl flex items-center justify-center">
            <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 5.87 2 10.5c0 2.3 1.1 4.4 2.9 5.9L4 20l4.3-2.3c1.1.3 2.4.5 3.7.5 5.52 0 10-3.87 10-8.5S17.52 2 12 2z"/>
              <circle cx="8" cy="10.5" r="1.5" fill="#005d7e"/>
              <circle cx="12" cy="10.5" r="1.5" fill="#005d7e"/>
              <circle cx="16" cy="10.5" r="1.5" fill="#005d7e"/>
            </svg>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              Install DP Chat
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Add to your home screen for quick access and offline support
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="shrink-0 p-1 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-sm"
            aria-label="Dismiss"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={handleDismiss}
            className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Not now
          </button>
          <button
            onClick={handleInstall}
            disabled={isInstalling}
            className="flex-1 px-3 py-2 text-sm font-medium text-white bg-decisiv-blue rounded-lg hover:bg-decisiv-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isInstalling ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Installing...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                </svg>
                Install
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to check PWA installation status
 */
export function usePWAStatus() {
  const [isInstalled, setIsInstalled] = useState(false);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // Check if running as PWA
    const checkInstalled = () => {
      const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                    window.navigator.standalone === true ||
                    document.referrer.includes('android-app://');
      setIsInstalled(isPWA);
    };

    checkInstalled();

    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', checkInstalled);

    // Listen for install availability
    const handleInstallAvailable = () => setCanInstall(true);
    window.addEventListener('pwa-install-available', handleInstallAvailable);

    // Listen for installation
    const handleInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
    };
    window.addEventListener('pwa-installed', handleInstalled);

    return () => {
      mediaQuery.removeEventListener('change', checkInstalled);
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('pwa-installed', handleInstalled);
    };
  }, []);

  return { isInstalled, canInstall };
}
